import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Label } from "../../components/ui/label";
import { X, Search, Edit, Trash, Plus, Tag, Check } from "lucide-react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { notesService, Note } from "../../lib/firebase";
import { toast } from "../../components/ui/use-toast";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

// Note Edit Dialog
interface NoteEditDialogProps {
  open: boolean;
  note: Note | null;
  onClose: () => void;
  onSave: (note: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>) => void;
}

const NoteEditDialog: React.FC<NoteEditDialogProps> = ({
  open,
  note,
  onClose,
  onSave
}) => {
  const [title, setTitle] = useState(note?.title || '');
  const [content, setContent] = useState(note?.content || '');
  const [tags, setTags] = useState<string[]>(note?.tags || []);
  const [newTag, setNewTag] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isPreview, setIsPreview] = useState(false);

  useEffect(() => {
    if (open) {
      setTitle(note?.title || '');
      setContent(note?.content || '');
      setTags(note?.tags || []);
      setNewTag('');
      setError(null);
      setIsPreview(false);
    }
  }, [open, note]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!title.trim()) {
      setError("Title is required");
      return;
    }
    
    onSave({
      title: title.trim(),
      content,
      tags
    });
  };

  const handleAddTag = () => {
    if (!newTag.trim()) return;
    
    // Don't add duplicates
    if (tags.includes(newTag.trim().toLowerCase())) {
      setError("Tag already exists");
      return;
    }

    setTags([...tags, newTag.trim().toLowerCase()]);
    setNewTag('');
    setError(null);
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags(tags.filter(tag => tag !== tagToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddTag();
    }
  };

  const togglePreview = () => {
    setIsPreview(!isPreview);
  };

  return (
    <Dialog open={open} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" aria-describedby="note-edit-description">
        <DialogHeader>
          <DialogTitle>{note ? 'Edit Note' : 'Create New Note'}</DialogTitle>
          <DialogDescription id="note-edit-description">
            {note ? 'Make changes to your note' : 'Create a new note'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="title">Title</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Enter note title"
            />
          </div>
          
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="content">Content</Label>
              <Button 
                type="button"
                variant="ghost" 
                size="sm" 
                onClick={togglePreview} 
                className="h-7 text-xs"
              >
                {isPreview ? 'Edit' : 'Preview'}
              </Button>
            </div>
            
            {isPreview ? (
              <div className="border rounded-md p-4 min-h-[200px] prose max-w-none overflow-auto">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>
              </div>
            ) : (
              <textarea
                id="content"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                className="w-full min-h-[200px] p-3 border rounded-md outline-none resize-y font-mono text-sm"
                placeholder="Write your note in Markdown format..."
              />
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="tags">Tags</Label>
            <div className="flex gap-2">
              <Input
                id="tags"
                value={newTag}
                onChange={(e) => setNewTag(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add tag"
                className="flex-1"
              />
              <Button
                type="button"
                onClick={handleAddTag}
                variant="outline"
                className="gap-2"
              >
                <Tag size={16} />
                Add
              </Button>
            </div>
            
            {error && <p className="text-sm text-red-500">{error}</p>}
            
            <div className="flex flex-wrap gap-2 mt-2">
              {tags.map((tag, index) => (
                <Badge 
                  key={index} 
                  variant="secondary"
                  className="flex items-center gap-1 px-2 py-1"
                >
                  {tag}
                  <X 
                    size={14} 
                    className="cursor-pointer" 
                    onClick={() => handleRemoveTag(tag)}
                  />
                </Badge>
              ))}
              {tags.length === 0 && (
                <p className="text-sm text-gray-500">No tags added</p>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">
              Save
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Note View Dialog for viewing full note content
interface NoteViewDialogProps {
  open: boolean;
  note: Note | null;
  onClose: () => void;
  onEdit: () => void;
}

const NoteViewDialog: React.FC<NoteViewDialogProps> = ({
  open,
  note,
  onClose,
  onEdit
}) => {
  if (!note) return null;

  return (
    <Dialog open={open} onOpenChange={open => !open && onClose()}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{note.title}</DialogTitle>
          <div className="text-xs text-gray-500">
            Last updated: {new Date(note.updatedAt || '').toLocaleString()}
          </div>
        </DialogHeader>
        
        <div className="py-4">
          <div className="prose max-w-none">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{note.content}</ReactMarkdown>
          </div>
          
          {note.tags.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-6">
              <span className="text-sm text-gray-500 mr-2">Tags:</span>
              {note.tags.map((tag, i) => (
                <Badge key={i} variant="outline">
                  {tag}
                </Badge>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          <Button onClick={onEdit}>
            Edit Note
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const ADMIN_EMAIL = "admin@rawpa.com";

export const AdminNotes = (): JSX.Element => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState<Note[]>([]);
  const [editNote, setEditNote] = useState<Note | null>(null);
  const [viewNote, setViewNote] = useState<Note | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Note[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [allTags, setAllTags] = useState<string[]>([]);
  const auth = getAuth();

  // Check authentication
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user || user.email !== ADMIN_EMAIL) {
        navigate('/aAyuiOcvMq');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, [navigate, auth]);

  // Load notes
  useEffect(() => {
    const loadNotes = async () => {
      setLoading(true);
      try {
        const data = await notesService.getNotes();
        setNotes(data);
        
        // Extract all unique tags
        const uniqueTags = Array.from(
          new Set(data.flatMap(note => note.tags))
        );
        setAllTags(uniqueTags);
      } catch (error) {
        console.error("Error loading notes:", error);
        toast({
          title: "Error",
          description: "Failed to load notes. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    loadNotes();
  }, []);

  const handleCreateNote = () => {
    setEditNote(null);
    setIsEditDialogOpen(true);
  };

  const handleEditNote = (note: Note) => {
    setEditNote(note);
    setIsEditDialogOpen(true);
  };

  const handleViewNote = (note: Note) => {
    setViewNote(note);
    setIsViewDialogOpen(true);
  };

  const handleEditFromView = () => {
    // Transfer the note from view to edit mode
    setEditNote(viewNote);
    setIsViewDialogOpen(false);
    setIsEditDialogOpen(true);
  };

  const handleDeleteNote = async (noteId: string) => {
    if (!window.confirm("Are you sure you want to delete this note? This cannot be undone.")) {
      return;
    }
    
    try {
      const success = await notesService.deleteNote(noteId);
      if (success) {
        setNotes(notes.filter(note => note.id !== noteId));
        toast({
          title: "Success",
          description: "Note deleted successfully",
        });
      } else {
        throw new Error("Failed to delete note");
      }
    } catch (error) {
      console.error("Error deleting note:", error);
      toast({
        title: "Error",
        description: "Failed to delete note. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSaveNote = async (noteData: Omit<Note, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      if (editNote) {
        // Update existing note
        const updatedNote = {
          ...noteData,
          id: editNote.id
        };
        
        const success = await notesService.updateNote(updatedNote);
        if (success) {
          // Update the note in the local state
          setNotes(notes.map(note => 
            note.id === editNote.id 
              ? { ...updatedNote, createdAt: editNote.createdAt, updatedAt: new Date().toISOString() } 
              : note
          ));
          
          toast({
            title: "Success",
            description: "Note updated successfully",
          });
        } else {
          throw new Error("Failed to update note");
        }
      } else {
        // Create new note
        const newNoteId = await notesService.createNote(noteData);
        if (newNoteId) {
          const now = new Date().toISOString();
          const newNote: Note = {
            ...noteData,
            id: newNoteId,
            createdAt: now,
            updatedAt: now
          };
          
          setNotes([newNote, ...notes]);
          
          toast({
            title: "Success",
            description: "Note created successfully",
          });
        } else {
          throw new Error("Failed to create note");
        }
      }
      
      // Update all tags
      const updatedTags = Array.from(
        new Set([...allTags, ...noteData.tags])
      );
      setAllTags(updatedTags);
      
      // Close the dialog
      setIsEditDialogOpen(false);
      setEditNote(null);
      
      // Clear search if active
      if (searchResults) {
        handleSearch();
      }
    } catch (error) {
      console.error("Error saving note:", error);
      toast({
        title: "Error",
        description: "Failed to save note. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim() && selectedTags.length === 0) {
      setSearchResults(null);
      return;
    }
    
    setIsSearching(true);
    try {
      let results = notes;
      
      // If there's a search query, filter by that
      if (searchQuery.trim()) {
        results = await notesService.searchNotes(searchQuery);
      }
      
      // If there are selected tags, filter by those too
      if (selectedTags.length > 0) {
        results = results.filter(note => 
          selectedTags.every(tag => note.tags.includes(tag))
        );
      }
      
      setSearchResults(results);
    } catch (error) {
      console.error("Error searching notes:", error);
      toast({
        title: "Error",
        description: "Failed to search notes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSearching(false);
    }
  };

  const toggleTagSelection = (tag: string) => {
    if (selectedTags.includes(tag)) {
      setSelectedTags(selectedTags.filter(t => t !== tag));
    } else {
      setSelectedTags([...selectedTags, tag]);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedTags([]);
    setSearchResults(null);
  };

  // Render the notes
  const renderNotes = () => {
    const displayNotes = searchResults !== null ? searchResults : notes;
    
    if (displayNotes.length === 0) {
      return (
        <div className="text-center py-8">
          <p className="text-gray-500">No notes found</p>
          {searchResults !== null && (
            <Button 
              onClick={clearFilters} 
              variant="outline" 
              className="mt-4"
            >
              Clear Filters
            </Button>
          )}
        </div>
      );
    }
    
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {displayNotes.map(note => (
          <Card key={note.id} className="overflow-hidden">
            <CardHeader className="p-4 pb-2">
              <CardTitle className="text-lg line-clamp-1">{note.title}</CardTitle>
              <div className="text-xs text-gray-500 mt-1">
                {new Date(note.updatedAt || '').toLocaleString()}
              </div>
            </CardHeader>
            <CardContent className="p-4 pt-0">
              <div className="prose prose-sm mb-3 h-24 overflow-hidden relative">
                <div className="line-clamp-4">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{note.content}</ReactMarkdown>
                </div>
                <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-white to-transparent"></div>
              </div>
              
              {note.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {note.tags.map((tag, i) => (
                    <Badge key={i} variant="outline" className="text-xs">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}
              
              <div className="flex justify-end gap-2 mt-3">
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-8 px-2"
                  onClick={() => handleViewNote(note)}
                >
                  View
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-8 px-2"
                  onClick={() => handleEditNote(note)}
                >
                  <Edit size={16} />
                </Button>
                <Button 
                  size="sm" 
                  variant="outline" 
                  className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => handleDeleteNote(note.id)}
                >
                  <Trash size={16} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      {/* Navigation bar */}
      <div className="fixed w-full px-4 top-[46px] z-10">
        <div className="max-w-[1257px] mx-auto h-[77px] relative rounded-[28px]">
          <div className="absolute w-full h-full top-0 left-0 bg-white rounded-[28px] shadow-[0px_4px_4px_#00000040] blur-[3.05px] opacity-[0.53]" />
          <div className="relative h-full flex items-center justify-between px-6">
            <div className="flex items-center gap-4 cursor-pointer" onClick={() => navigate('/aAyuiOcvMq/dashboard')}>
              <img className="w-[27px] h-[27px] object-cover" alt="Beetle" src="/beetle.png" />
              <span className="font-['Space_Grotesk'] font-bold text-black text-[32px]">RAWPA Admin</span>
            </div>
            <div 
              onClick={() => {
                if (auth) auth.signOut().then(() => navigate('/aAyuiOcvMq'));
              }}
              className="w-[48px] h-[48px] bg-[#daed6e] rounded-[12px] flex items-center justify-center cursor-pointer hover:bg-[#c5d85c] transition-colors"
            >
              <img className="w-6 h-6" alt="Sign Out" src="/user-icon.svg" />
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-16">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => navigate('/aAyuiOcvMq/dashboard')}
              className="text-black opacity-60 hover:opacity-100 transition-opacity"
            >
              ← Back to Dashboard
            </button>
          </div>
          
          <Button
            onClick={handleCreateNote}
            className="h-9 px-4 bg-[#daed6e] rounded-lg hover:bg-[#c5d85c] transition-colors"
          >
            <Plus size={18} className="mr-2" />
            New Note
          </Button>
        </div>
        
        <div className="bg-white rounded-[20px] p-6 shadow-lg">
          <div className="mb-6">
            <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
              Admin Notes
            </h1>
            <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
              Create and manage your admin notes
            </p>
          </div>
          
          {/* Search and filter */}
          <div className="mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Search & Filter</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col md:flex-row gap-2 mb-4">
                  <div className="flex gap-2 flex-1">
                    <Input
                      placeholder="Search notes..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          handleSearch();
                        }
                      }}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleSearch}
                      disabled={isSearching}
                      className="bg-[#daed6e] hover:bg-[#c5d85c] text-black"
                    >
                      <Search size={18} className="mr-2" />
                      {isSearching ? "Searching..." : "Search"}
                    </Button>
                  </div>
                  
                  {searchResults !== null && (
                    <Button 
                      variant="outline" 
                      onClick={clearFilters}
                      className="whitespace-nowrap"
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
                
                {/* Tags filter */}
                {allTags.length > 0 && (
                  <div>
                    <Label className="mb-2 block">Filter by Tags</Label>
                    <div className="flex flex-wrap gap-2">
                      {allTags.map((tag, index) => (
                        <Badge 
                          key={index} 
                          variant={selectedTags.includes(tag) ? "default" : "outline"}
                          className={`cursor-pointer ${selectedTags.includes(tag) ? 'bg-[#daed6e] text-black hover:bg-[#c5d85c]' : 'hover:bg-gray-100'}`}
                          onClick={() => toggleTagSelection(tag)}
                        >
                          {selectedTags.includes(tag) && <Check size={12} className="mr-1" />}
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    
                    {selectedTags.length > 0 && (
                      <div className="mt-2 flex justify-end">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => {
                            setSelectedTags([]);
                            if (selectedTags.length > 0) handleSearch();
                          }}
                          className="h-7 text-xs"
                        >
                          Clear Tag Selection
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
          
          {/* Notes grid */}
          {renderNotes()}
        </div>
      </div>
      
      {/* Edit Note Dialog */}
      <NoteEditDialog
        open={isEditDialogOpen}
        note={editNote}
        onClose={() => setIsEditDialogOpen(false)}
        onSave={handleSaveNote}
      />
      
      {/* View Note Dialog */}
      <NoteViewDialog
        open={isViewDialogOpen}
        note={viewNote}
        onClose={() => setIsViewDialogOpen(false)}
        onEdit={handleEditFromView}
      />
    </div>
  );
}; 